import React, { useCallback, useEffect, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import Loading from "components/Loading";
import ZipSlotAdsTabs from "components/dashboard/zipSlotAds/ZipSlotAdsTabs";
import TopUpDialog from "components/dashboard/zipSlotAds/TopUpDialog";
import AutoRechargeForm from "components/dashboard/zipSlotAds/AutoRechargeForm";
import TransactionList from "components/dashboard/zipSlotAds/TransactionList";
import {
  dollarsToCents,
  formatCents,
  isLowBalance,
  callableErrorMessage,
  resolveInstitutionId,
  uniqueRelatedZips,
} from "components/dashboard/zipSlotAds/zipSlotAdsHelpers";
import { BID_STATUS } from "components/dashboard/zipSlotAds/zipSlotAdsConstants";
import { useAuth } from "hooks/useAuth";
import {
  getOwnZipSlotBid,
  getUserPaymentMethod,
  listenAdBalance,
  listLedgerPage,
  topUpBalance,
  updateAdAutoRecharge,
} from "services/zipSlotAds/zipSlotAdsService";

const AdBalancePage = () => {
  const { userData, tokenClaims, organizationId, userLoading } = useAuth();
  const institutionId = resolveInstitutionId({
    userData,
    tokenClaims,
    organizationId,
  });

  const [balance, setBalance] = useState(null);
  const [balanceLoadError, setBalanceLoadError] = useState("");
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLastDoc, setTxLastDoc] = useState(null);
  const [txHasMore, setTxHasMore] = useState(false);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState("");
  const [highestActiveBidCents, setHighestActiveBidCents] = useState(0);

  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpSubmitting, setTopUpSubmitting] = useState(false);
  const [topUpError, setTopUpError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);

  const [autoSaving, setAutoSaving] = useState(false);
  const [autoError, setAutoError] = useState("");
  const [autoSuccess, setAutoSuccess] = useState("");
  const [topUpSuccess, setTopUpSuccess] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("topup_success") === "true") {
      setTopUpSuccess(
        "Checkout completed. Your balance will update when the payment webhook finishes.",
      );
      const nextUrl = window.location.pathname;
      window.history.replaceState({}, document.title, nextUrl);
    }
  }, []);

  useEffect(() => {
    if (!institutionId) {
      setBalanceLoaded(true);
      return undefined;
    }
    setBalanceLoaded(false);
    const unsub = listenAdBalance(
      institutionId,
      (doc) => {
        setBalance(doc);
        setBalanceLoadError("");
        setBalanceLoaded(true);
      },
      (err) => {
        setBalanceLoadError(
          callableErrorMessage(err, "Unable to load ad balance."),
        );
        setBalanceLoaded(true);
      },
    );
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [institutionId]);

  const loadTransactions = useCallback(
    async ({ append } = { append: false }) => {
      if (!institutionId) return;
      setTxLoading(true);
      setTxError("");
      try {
        const page = await listLedgerPage({
          institutionId,
          startAfterDoc: append ? txLastDoc : null,
        });
        const rows = Array.isArray(page?.rows) ? page.rows : [];
        setTransactions((prev) => (append ? [...prev, ...rows] : rows));
        setTxLastDoc(page?.lastDoc || null);
        setTxHasMore(Boolean(page?.hasMore));
      } catch (err) {
        setTxError(
          callableErrorMessage(err, "Unable to load balance transactions."),
        );
      } finally {
        setTxLoading(false);
      }
    },
    [institutionId, txLastDoc],
  );

  useEffect(() => {
    if (institutionId) {
      loadTransactions({ append: false });
    }
    // Initial load only when institution changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId]);

  useEffect(() => {
    if (!institutionId) return undefined;
    const zips = uniqueRelatedZips(transactions);
    if (zips.length === 0) {
      setHighestActiveBidCents(0);
      return undefined;
    }
    let cancelled = false;
    Promise.all(zips.map((zip) => getOwnZipSlotBid(zip, institutionId)))
      .then((bids) => {
        if (cancelled) return;
        const highest = bids.reduce((max, bid) => {
          if (!bid || bid.status !== BID_STATUS.ACTIVE) return max;
          return Math.max(max, Number(bid.bidPerClickCents) || 0);
        }, 0);
        setHighestActiveBidCents(highest);
      })
      .catch(() => {
        if (!cancelled) setHighestActiveBidCents(0);
      });
    return () => {
      cancelled = true;
    };
  }, [institutionId, transactions]);

  const fetchPaymentMethod = async () => {
    setPaymentMethodLoading(true);
    try {
      const result = await getUserPaymentMethod();
      setPaymentMethod(result);
    } catch (err) {
      setPaymentMethod({
        success: false,
        error: "unknown",
        message: callableErrorMessage(
          err,
          "Unable to retrieve payment information.",
        ),
      });
    } finally {
      setPaymentMethodLoading(false);
    }
  };

  const openTopUp = () => {
    setTopUpError("");
    setTopUpOpen(true);
    fetchPaymentMethod();
  };

  const submitTopUp = async (dollarAmount) => {
    const amountCents = dollarsToCents(dollarAmount);
    if (amountCents == null) {
      setTopUpError("Enter a valid amount.");
      return;
    }
    setTopUpSubmitting(true);
    setTopUpError("");
    try {
      const origin = window.location.origin;
      const result = await topUpBalance({
        amountCents,
        institutionId,
        successUrl: `${origin}/dashboard/advertising/balance?topup_success=true`,
        cancelUrl: `${origin}/dashboard/advertising/balance?topup_canceled=true`,
      });
      if (result?.url) {
        window.location.href = result.url;
        return;
      }
      setTopUpError("No checkout URL was returned.");
    } catch (err) {
      setTopUpError(callableErrorMessage(err, "Failed to start top-up."));
    } finally {
      setTopUpSubmitting(false);
    }
  };

  const saveAutoRecharge = async (form) => {
    setAutoSaving(true);
    setAutoError("");
    setAutoSuccess("");
    try {
      const enabled = Boolean(form.autoRechargeEnabled);
      await updateAdAutoRecharge({
        institutionId,
        autoRechargeEnabled: enabled,
        autoRechargeThresholdCents: enabled
          ? dollarsToCents(form.autoRechargeThresholdDollars)
          : null,
        autoRechargeAmountCents: enabled
          ? dollarsToCents(form.autoRechargeAmountDollars)
          : null,
      });
      setAutoSuccess("Auto-recharge settings saved.");
    } catch (err) {
      setAutoError(
        callableErrorMessage(err, "Failed to save auto-recharge settings."),
      );
    } finally {
      setAutoSaving(false);
    }
  };

  const balanceCents = Number(balance?.balanceCents) || 0;
  const lowBalance = isLowBalance(balanceCents, highestActiveBidCents);
  const hasPaymentMethod = Boolean(
    balance?.defaultPaymentMethodId || paymentMethod?.success,
  );

  if (userLoading || (institutionId && !balanceLoaded)) {
    return <Loading page />;
  }

  if (!institutionId) {
    return (
      <Container maxWidth="lg">
        <DashboardPageHeader title="Ad balance" subtitle="Prepaid CPC budget." />
        <ZipSlotAdsTabs />
        <Alert severity="warning">
          No institution is linked to your account.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <DashboardPageHeader
        title="Ad balance"
        subtitle="Prepaid click budget. Balance changes only through top-up, auto-recharge, click charges, and refunds — never from this page directly."
      />
      <ZipSlotAdsTabs />

      {topUpSuccess ? (
        <Alert
          severity="success"
          onClose={() => setTopUpSuccess("")}
          sx={{ mb: 3 }}
        >
          {topUpSuccess}
        </Alert>
      ) : null}

      {balanceLoadError ? (
        <DataLoadErrorAlert message={balanceLoadError} />
      ) : null}

      {lowBalance ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Balance ({formatCents(balanceCents)}) is below your highest active
          bid ({formatCents(highestActiveBidCents)}). Ads can be pulled from
          rotation until you top up.
        </Alert>
      ) : null}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Current balance
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {formatCents(balanceCents)}
            </Typography>
          </Box>
          <LoadingButton variant="contained" onClick={openTopUp}>
            Top up
          </LoadingButton>
        </Stack>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <AutoRechargeForm
          balance={balance}
          onSave={saveAutoRecharge}
          saving={autoSaving}
          error={autoError}
          success={autoSuccess}
          onDismissSuccess={() => setAutoSuccess("")}
          hasPaymentMethod={
            hasPaymentMethod || Boolean(balance?.defaultPaymentMethodId)
          }
        />
      </Box>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Recent transactions
      </Typography>
      {txError ? (
        <DataLoadErrorAlert
          message={txError}
          onRetry={() => loadTransactions({ append: false })}
        />
      ) : (
        <TransactionList
          rows={transactions}
          loadingMore={txLoading}
          hasMore={txHasMore}
          onLoadMore={() => loadTransactions({ append: true })}
        />
      )}

      <TopUpDialog
        open={topUpOpen}
        onClose={() => !topUpSubmitting && setTopUpOpen(false)}
        submitting={topUpSubmitting}
        error={topUpError}
        paymentMethod={paymentMethod}
        paymentMethodLoading={paymentMethodLoading}
        onSubmit={submitTopUp}
      />
    </Container>
  );
};

export default AdBalancePage;
